import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

type AlertContextType = {
  showAlert: (options: AlertOptions) => void;
  closeAlert: () => void;
};

type AlertOptions = {
  text: string;
  severity?: AlertColor;
};

type AlertState = {
  open: boolean;
  text: string;
  severity: AlertColor;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

type AlertProviderProps = {
  children: ReactNode;
};

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    text: '',
    severity: 'success',
  });

  const showAlert = useCallback(
    ({ severity = 'success', text }: AlertOptions) => {
      setAlert({ open: true, text, severity });
    },
    []
  );

  const closeAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, closeAlert }}>
      {children}
      <Snackbar
        sx={{ px: 2 }}
        open={alert.open}
        autoHideDuration={3500}
        onClose={closeAlert}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Alert
          onClose={closeAlert}
          severity={alert.severity}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {alert.text}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
